import React, { Component, Fragment } from 'react';
import cn from 'classnames';
import nanoid from 'nanoid';
import moment from 'moment';
import times from 'lodash/times';
import range from 'lodash/range';
import minBy from 'lodash/minBy';
import maxBy from 'lodash/maxBy';
import padStart from 'lodash/padStart';
import upperFirst from 'lodash/upperFirst';

import { getUserFullName } from '../../utils/utilsChat';
import {
  inflect,
  withMomentLocale,
  isWithinElement,
  scrollToElement,
  exposeComponentToGlobalScope,
} from '../../utils/utilsGlobal';

import styles from './ScheduleSettingsCalendar.scss';

export class ScheduleSettingsCalendar extends Component {

  state = {
    columns: [],
    calendar: [],
    hoursScale: [],
    heatmapColorScale: {},
    isDisplayingHeatmap: false,
    heatmapBalloon: null,
    didScrollToCenter: false,
  };

  defaultCellToScrollTo = React.createRef();

  CELL_WIDTH = 80;
  CELL_HEIGHT = 40;
  HOURS_SCALE_WIDTH = 70;
  DISPLAYED_HOURS_PER_DAY = 24;

  HEATMAP_BALLOON_NUMBER_OF_OPERATORS = 3;
  HEATMAP_MIN_COLOR = '#D8E4FB';
  HEATMAP_MAX_COLOR = '#0033FF';

  componentDidMount(){
    document.body.addEventListener('click', this.onBodyClick);

    this.setState({
      hoursScale: this.generateHoursScale(),
    });
    this.onScheduleChange();
    exposeComponentToGlobalScope(this);
  }

  componentDidUpdate(prevProps){
    const { schedule } = this.props;
    if (schedule !== prevProps.schedule) {
      this.onScheduleChange();
    }
  }

  componentWillUnmount(){
    document.body.removeEventListener('click', this.onBodyClick);
  }

  onScheduleChange = () => {
    const { month, schedule, isHeatmap } = this.props;
    const { didScrollToCenter } = this.state;

    let columns = [];
    let calendar = [];
    let heatmapColorScale = {};
    if (month) {
      columns = this.generateColumns(month);
      calendar = this.generateCalendar(schedule, isHeatmap);

      if (isHeatmap) {
        const heatmapMinValue = minBy(calendar, 'workingOperatorsCount')?.workingOperatorsCount;
        const heatmapMaxValue = maxBy(calendar, 'workingOperatorsCount')?.workingOperatorsCount;
        heatmapColorScale = this.generateHeatmapColorScale(heatmapMinValue, heatmapMaxValue);
      }
    }

    this.setState({
      columns,
      calendar,
      heatmapColorScale,
      isDisplayingHeatmap: isHeatmap,
    }, () => {
      if (month && !didScrollToCenter) {
        scrollToElement(this.defaultCellToScrollTo.current, null, {
          behavior: 'auto',
          block: 'center',
          inline: 'center',
        });
        this.setState({ didScrollToCenter: true });
      }
    });
  };

  getYearByMonthName = (monthName) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonthIndex = now.getMonth();
    const monthIndex = moment(monthName, 'MMMM').get('month');
    return monthIndex < currentMonthIndex ? currentYear + 1 : currentYear;
  };

  getWeekdayByIndex = (dayIndex) => {
    return withMomentLocale('ru', () => {
      return upperFirst(moment().day(dayIndex).format('ddd'));
    });
  };

  generateColumns = (monthName) => {
    return withMomentLocale('en', () => {
      const columns = [];
      const monthYear = this.getYearByMonthName(monthName);
      const currentDate = moment().date(1).month(monthName).year(monthYear);

      let index = 0;
      let currentDateMonthName = monthName;

      while (currentDateMonthName === monthName) {
        const weekdayIndex = currentDate.get('day');
        columns.push({
          date: currentDate.get('date'),
          weekday: this.getWeekdayByIndex(weekdayIndex),
          isWeekend: weekdayIndex === 0 || weekdayIndex === 6,
          isToday: currentDate.isSame(new Date(), 'day'),
          offsetLeft: this.CELL_WIDTH * index + this.HOURS_SCALE_WIDTH,
          index,
        });
        currentDate.add(1, 'day');
        currentDateMonthName = currentDate.format('MMMM').toLowerCase();
        index++;
      }
      return columns;
    });
  };

  generateHoursScale = () => {
    return times(this.DISPLAYED_HOURS_PER_DAY).map(index => {
      const hours = index === 24
        ? '00'
        : padStart(index, 2, '0');
      return hours + ':00';
    });
  };

  generateCalendar = (schedule, isHeatmap) => {
    let periods = [];

    schedule.forEach(scheduleItem => {
      const { beginTimestamp, endTimestamp, operators, workingOperatorsCount, breaks } = scheduleItem;
      let periodFrom = beginTimestamp;
      if (isHeatmap && !operators?.length) {
        return;
      }

      if (breaks?.length) {
        breaks.sort((a,b) => {
          return new Date(a.beginTimestamp) < new Date(b.beginTimestamp) ? -1 : 1;
        });
        breaks.forEach(breakItem => {
          periods = periods.concat(
            this.getCalendarEntries(periodFrom, breakItem.beginTimestamp, { operators, workingOperatorsCount })
          );
          periodFrom = breakItem.endTimestamp;
        });
      }
      periods = periods.concat(
        this.getCalendarEntries(periodFrom, endTimestamp, { operators, workingOperatorsCount })
      );
    });

    return periods;
  };

  getCalendarEntries = (beginTimestamp, endTimestamp, optionalData) => {
    const id = nanoid();
    const dateFrom = new Date(beginTimestamp);
    const dateTo = new Date(endTimestamp);

    const timeFrom = {
      h: dateFrom.getHours(),
      m: dateFrom.getMinutes(),
    };
    const timeTo = {
      h: dateTo.getHours(),
      m: dateTo.getMinutes(),
    };
    const hoursFrom = timeFrom.h + timeFrom.m / 60;
    const hoursTo = timeTo.h + timeTo.m / 60;
    const offsetLeft = (dateFrom.getDate() - 1) * this.CELL_WIDTH;
    const offsetTop = hoursFrom * this.CELL_HEIGHT;
    const labelFrom = padStart(timeFrom.h, 2, '0') + ':' + padStart(timeFrom.m, 2, '0');
    const labelTo = padStart(timeTo.h, 2, '0') + ':' + padStart(timeTo.m, 2, '0');

    const height = (hoursTo - hoursFrom) * this.CELL_HEIGHT;
    const extendsToNextDay = !moment(dateFrom).isSame(new Date(dateTo - 1), 'day');

    if (extendsToNextDay) {
      const firstEntryTo = moment(dateFrom).endOf('day').toDate();
      const firstEntry = this.getCalendarEntries(dateFrom, firstEntryTo, {
        ...optionalData,
        endsTomorrow: true,
        to: dateTo,
        labelTo,
      })[0];

      const secondsEntryFrom = moment(dateTo).startOf('day').toDate();
      const secondEntry = this.getCalendarEntries(secondsEntryFrom, dateTo, {
        ...optionalData,
        startsYesterday: true,
        from: dateFrom,
        labelFrom,
      })[0];

      return [firstEntry, secondEntry]
    }
    else {
      return [{
        id,
        from: dateFrom,
        to: dateTo,
        labelFrom,
        labelTo,
        height,
        offsetTop,
        offsetLeft: Math.round(offsetLeft),
        ...optionalData,
      }];
    }
  };

  generateHeatmapColorScale = (minValue, maxValue) => {
    if (minValue === maxValue) {
      return {
        [maxValue]: this.HEATMAP_MAX_COLOR,
      };
    }
    const maxValueWithGap = maxValue + 1;
    const canvasWidth = maxValueWithGap - minValue;
    const canvasHeight = 1;

    const canvas = document.createElement('canvas');
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    const ctx = canvas.getContext('2d');
    const gradient = ctx.createLinearGradient(0, 0, canvasWidth, 0);
    gradient.addColorStop(0, this.HEATMAP_MIN_COLOR);
    gradient.addColorStop(1, this.HEATMAP_MAX_COLOR);

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    const heatmapColorScale = {};

    range(minValue, maxValueWithGap).forEach(value => {
      const canvasXPos = value - minValue;
      const imageData = ctx.getImageData(canvasXPos, 0, 1, 1).data;
      heatmapColorScale[value] = `rgb(${imageData.slice(0, 3).join(', ')})`;
    });

    return heatmapColorScale;
  };

  onBodyClick = (e) => {
    if (!isWithinElement(e.target, styles.heatmapBalloonExpand)) {
      this.setState({
        heatmapBalloon: null,
        isBalloonListExpanded: false,
      });
    }
  };

  onHeatmapEntryClick = (entry) => {
    this.setState({
      heatmapBalloon: entry,
      isBalloonListExpanded: false,
    });
  };

  shouldRenderStraightBottomCorners = (entry, nextEntry) => {
    const isAlignedHorizontally = entry.offsetLeft === nextEntry?.offsetLeft;
    const isAlignedVertically = entry.offsetTop + entry.height >= nextEntry?.offsetTop - 1;
    return isAlignedHorizontally && isAlignedVertically;
  };

  renderBalloon = (entry) => {
    const { isBalloonListExpanded } = this.state;
    const allOperators = entry.operators;
    const shortListOperators = allOperators.slice(0, this.HEATMAP_BALLOON_NUMBER_OF_OPERATORS);
    const remainingOperators = allOperators.slice(this.HEATMAP_BALLOON_NUMBER_OF_OPERATORS);
    const shouldDisplayFullList = isBalloonListExpanded || allOperators.length <= this.HEATMAP_BALLOON_NUMBER_OF_OPERATORS;
    const balloonMaxHeight = Math.min(entry.offsetTop - 10, 300);

    return (
      <div className={cn({
        [styles.heatmapBalloon]: true,
        [styles.heatmapBalloonAlignRight]: entry.offsetTop < 120,
      })} style={{
        top: entry.offsetTop,
        left: entry.offsetLeft,
      }}>
        <div className={styles.heatmapBalloonInner} style={{ maxHeight: balloonMaxHeight }}>
          <div className={styles.heatmapBalloonTime}>
            {entry.labelFrom} — {entry.labelTo}
          </div>
          {inflect(allOperators.length, ['оператор', 'оператора', 'операторов'])}
          {Boolean(remainingOperators.length && !shouldDisplayFullList) && ', в том числе '}
          {!shouldDisplayFullList && (
            <Fragment>
              {shortListOperators.map(operator => getUserFullName(operator)).join(', ')}
              {Boolean(remainingOperators.length) && (
                <span className={styles.heatmapBalloonExpand}
                  onClick={() => this.setState({isBalloonListExpanded: true})}>
                &nbsp;и&nbsp;{inflect(remainingOperators.length, ['другой', 'других', 'других'])}
              </span>
              )}
            </Fragment>
          )}
          {shouldDisplayFullList && (
            <ul className={styles.heatmapBalloonFullList}>
              {allOperators.map(operator => (
                <li key={operator.id}>{getUserFullName(operator)}</li>
              ))}
            </ul>
          )}
        </div></div>
    );
  };

  render(){
    const { className, isLoading } = this.props;
    const {
      columns,
      calendar,
      hoursScale,
      heatmapColorScale,
      isDisplayingHeatmap,
      heatmapBalloon,
    } = this.state;

    const columnHours = times(this.DISPLAYED_HOURS_PER_DAY);
    const scrollableAreaWidth = this.CELL_WIDTH * columns.length + this.HOURS_SCALE_WIDTH * 2;

    return (
      <div className={cn({
        [className]: true,
        [styles.container]: true,
        [styles.containerLoading]: isLoading,
      })}>
        <div style={{ width: scrollableAreaWidth }}>
          <div className={cn(styles.hoursScale)}>
            {hoursScale.map((hoursScaleItem, i) => (
              <div className={styles.hoursScaleItem} key={i}>
                {hoursScaleItem}
                <div className={styles.hoursScaleItemRight}>{hoursScaleItem}</div>
              </div>
            ))}
          </div>
          {columns.map((column, columnIndex) => (
            <div key={column.date} style={{ left: column.offsetLeft }} className={cn({
              [styles.column]: true,
              [styles.columnFirst]: columnIndex === 0,
              [styles.columnLast]: columnIndex === columns.length - 1,
              [styles.columnWeekend]: column.isWeekend,
            })}>
              <div className={cn({
                [styles.columnHead]: true,
                [styles.columnHeadToday]: column.isToday,
              })}>
                {column.weekday}, {column.date}
              </div>
              {columnHours.map(hourIndex => (
                <div className={styles.columnDay}
                  key={hourIndex}
                  ref={
                    column.isToday && hourIndex === 12
                      ? this.defaultCellToScrollTo
                      : null
                  }/>
              ))}
            </div>
          ))}

          <div className={styles.calendar}>
            {calendar.map((calendarEntry, i) => (
              <Fragment key={calendarEntry.id}>
                <div className={cn({
                    [styles.calendarEntry]: true,
                    [styles.calendarEntrySmall]: calendarEntry.height < 42,
                    [styles.calendarEntryStartsYesterday]: calendarEntry.startsYesterday,
                    [styles.calendarEntryEndsTomorrow]: calendarEntry.endsTomorrow,
                    [styles.calendarEntryHeatmap]: isDisplayingHeatmap,
                    [styles.calendarEntryHeatmapStraightBottomCorners]: this.shouldRenderStraightBottomCorners(calendarEntry, calendar[i + 1]),
                  })}
                  style={{
                    top: calendarEntry.offsetTop,
                    left: calendarEntry.offsetLeft,
                    height: calendarEntry.height,
                    backgroundColor: isDisplayingHeatmap
                      ? heatmapColorScale[calendarEntry.workingOperatorsCount]
                      : null
                  }}
                  onClick={() => isDisplayingHeatmap && this.onHeatmapEntryClick(calendarEntry)}>
                  {!isDisplayingHeatmap && (
                    calendarEntry.labelFrom + ' — ' + calendarEntry.labelTo
                  )}
                </div>
                {(isDisplayingHeatmap && heatmapBalloon?.id === calendarEntry.id) && (
                  this.renderBalloon(calendarEntry)
                )}
              </Fragment>
            ))}
          </div>

        </div>
      </div>
    );
  }
}
